import { createContext, useContext, useEffect, useState } from 'react';

/**
 * ThemeContext
 * Manages high-contrast mode and XL font scale accessibility settings
 * Persists settings to localStorage and syncs with document data attributes
 */

const KEY_CONTRAST = 'sq_theme_high_contrast';
const KEY_FONT = 'sq_theme_font_scale';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [highContrast, setHighContrastState] = useState(() => {
    return localStorage.getItem(KEY_CONTRAST) === '1';
  });

  const [fontScale, setFontScaleState] = useState(() => {
    const saved = localStorage.getItem(KEY_FONT);
    return saved === 'xl' ? 'xl' : 'normal';
  });

  // Sync high contrast to document and localStorage
  useEffect(() => {
    localStorage.setItem(KEY_CONTRAST, highContrast ? '1' : '0');
    document.documentElement.dataset.contrast = highContrast ? 'high' : 'normal';
  }, [highContrast]);

  // Sync font scale to document and localStorage
  useEffect(() => {
    localStorage.setItem(KEY_FONT, fontScale);
    document.documentElement.dataset.fontscale = fontScale;
  }, [fontScale]);

  const setHighContrast = (value) => setHighContrastState(!!value);
  const setFontScale = (value) => setFontScaleState(value === 'xl' ? 'xl' : 'normal');

  const value = {
    highContrast,
    fontScale,
    setHighContrast,
    setFontScale,
    // Convenience toggles
    toggleContrast: () => setHighContrast(!highContrast),
    toggleFontScale: () => setFontScale(fontScale === 'xl' ? 'normal' : 'xl'),
    // Legacy API compatibility
    contrast: highContrast ? 'high' : 'normal',
    setContrast: (val) => setHighContrast(val === 'high')
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
